/**
 * WeatherHero — colorful, vibrant "Be the Local Weatherman" hero band
 * for /weather. Shows live platform stats, a recent-community-activity
 * strip, and a 3-step "How you can help in 30 seconds" card.
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import {
  Sun, Sunrise, Sunset, CloudRain, CloudLightning, Snowflake, Wind, Cloud,
  Users, Trophy, MapPin, ShieldCheck, Camera, ThumbsUp, BellRing, ArrowRight,
} from "lucide-react";
import CountUp from "@/components/CountUp";

const EVENT_ICON = {
  thunderstorm: CloudLightning, lightning: CloudLightning,
  heavy_rain: CloudRain, rain: CloudRain, light_rain: CloudRain,
  flood_risk: CloudRain, blizzard: Snowflake, snowstorm: Snowflake,
  snowfall: Snowflake, ice_storm: Snowflake, hail: Snowflake,
  frost: Snowflake, fog: Cloud, tornado: Wind, hurricane: Wind,
  cyclone: Wind, dust_storm: Wind, high_wind: Wind, wildfire_smoke: Sun,
  extreme_heat: Sun, heat_wave: Sun, extreme_cold: Snowflake,
};

const REPORT_ICON = {
  heavy_rain: CloudRain, flooding: CloudRain, flash_flood: CloudRain,
  thunderstorm: CloudLightning, lightning: CloudLightning,
  snow: Snowflake, heavy_snow: Snowflake, blizzard: Snowflake, ice_storm: Snowflake,
  hail: Snowflake, wildfire: Sun, smoke: Sun, extreme_heat: Sun, heat_wave: Sun,
  tornado: Wind, hurricane: Wind, cyclone: Wind, high_wind: Wind, dust_storm: Wind,
  fog: Cloud, downed_tree: Wind, landslide: CloudRain, extreme_cold: Snowflake,
};

const SEVERITY_COLOR = {
  mild:     "#22c55e",
  moderate: "#f59e0b",
  severe:   "#f97316",
  critical: "#dc2626",
};

function _fmt(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000)     return (n / 1_000).toFixed(1)   + "K";
  return String(n);
}
function _titleize(s) {
  return String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function _ago(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60)      return `${s}s ago`;
  if (s < 3600)    return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)   return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800)  return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function WeatherHero() {
  const [stats,    setStats]    = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, a] = await Promise.all([
          http.get("/weather-almanac/stats"),
          http.get("/weather-almanac/community-activity?limit=10"),
        ]);
        if (!cancelled) {
          setStats(s.data || {});
          setActivity(a.data?.items || []);
        }
      } catch { /* stats are best-effort — hide gracefully */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const statCards = useMemo(() => ([
    { label: "Localities",         value: stats?.localities,       icon: MapPin,     accent: "#0284c7" },
    { label: "Countries",          value: stats?.countries,        icon: Users,      accent: "#d97706" },
    { label: "Days of history",    value: stats?.days_stored,      icon: Trophy,     accent: "#7c3aed" },
    { label: "Community reports",  value: stats?.reports_total,    icon: Camera,     accent: "#ea580c" },
    { label: "Verified reports",   value: stats?.reports_verified, icon: ShieldCheck,accent: "#16a34a" },
    { label: "Reports this month", value: stats?.reports_30d,      icon: BellRing,   accent: "#e11d48" },
  ]), [stats]);

  return (
    <section className="relative overflow-hidden rounded-3xl border p-6 sm:p-10 mt-6"
             style={{
               borderColor: "var(--wofy-border)",
               background: "linear-gradient(135deg, #FEF3C7 0%, #FDF2F8 45%, #DBEAFE 100%)",
             }}
             data-testid="weather-hero">
      {/* Playful weather doodles in the corners */}
      <Sun     className="absolute -top-6 -right-6 w-40 h-40 opacity-25 text-amber-400 rotate-12 pointer-events-none" />
      <Sunrise className="absolute top-6 left-6 w-16 h-16 opacity-30 text-orange-500 pointer-events-none hidden md:block" />
      <Sunset  className="absolute bottom-6 right-24 w-16 h-16 opacity-30 text-rose-500 pointer-events-none hidden md:block" />
      <Cloud   className="absolute -bottom-8 -left-8 w-40 h-40 opacity-25 text-sky-500 pointer-events-none" />
      <Snowflake className="absolute top-1/2 right-1/3 w-10 h-10 opacity-25 text-sky-400 pointer-events-none hidden lg:block" />
      <CloudRain className="absolute bottom-16 left-1/3 w-12 h-12 opacity-20 text-blue-500 pointer-events-none hidden lg:block" />

      <div className="relative z-10">
        <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
          🌦️ JOIN THE COMMUNITY WEATHER TEAM
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
            style={{ color: "#1f2937" }}>
          Be the <span style={{ color: "#B25D38" }}>Local Weatherman</span>.<br />
          Record yesterday. Understand today. Prepare for tomorrow.
        </h2>
        <p className="mt-4 max-w-2xl text-base sm:text-lg" style={{ color: "#374151" }}>
          Snap a photo of the storm, verify a neighbour's report, get local
          alerts. Every observation becomes part of a growing, community-owned
          weather history that helps farmers, families and first responders.
        </p>

        {/* Live stat counters */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
             data-testid="weather-hero-stats">
          {statCards.map(({ label, value, icon: Icon, accent }) => (
            <div key={label}
                 className="rounded-2xl bg-white/70 backdrop-blur border p-3 flex flex-col items-start"
                 style={{ borderColor: "rgba(0,0,0,0.06)" }}
                 data-testid={`weather-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                   style={{ background: `${accent}15`, color: accent }}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="mt-2 font-display text-2xl leading-none font-bold"
                   style={{ color: accent }}>
                <CountUp value={value} duration={1400} />
              </div>
              <div className="mt-1 text-[11px] uppercase font-mono tracking-wider opacity-70">
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Recent event pills */}
        {stats?.recent_events?.length > 0 && (
          <div className="mt-6" data-testid="weather-hero-recent-events">
            <div className="text-xs uppercase font-mono tracking-wider opacity-70 mb-2">
              What's happening around the world (last 30 days)
            </div>
            <div className="flex flex-wrap gap-1.5">
              {stats.recent_events.map((e) => {
                const Icon = EVENT_ICON[e.tag] || Cloud;
                return (
                  <span key={e.tag}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-white/80 border"
                        style={{ borderColor: "rgba(0,0,0,0.08)" }}
                        data-testid={`weather-event-pill-${e.tag}`}>
                    <Icon className="w-3 h-3" style={{ color: "#B25D38" }} />
                    {_titleize(e.tag)}
                    <span className="font-mono opacity-60">· {e.count}</span>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* CTAs */}
        <div className="mt-6 flex flex-wrap gap-3">
          <a href="#community-reports"
             className="wofy-btn-primary"
             onClick={(e) => {
               e.preventDefault();
               document.getElementById("community-reports")?.scrollIntoView({ behavior: "smooth" });
             }}
             data-testid="weather-hero-report-cta">
            <Camera className="w-4 h-4" /> Report Local Weather
          </a>
          <Link to="/weather/farmer" className="wofy-btn-ghost text-sm"
                data-testid="weather-hero-farmer">
            🌱 Farmer's Dashboard → <ArrowRight className="w-3 h-3" />
          </Link>
          <Link to="/weather/about" className="wofy-btn-ghost text-sm"
                data-testid="weather-hero-about">
            Why it matters → <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Three-step "how you can help in 30 seconds" */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3"
             data-testid="weather-hero-how-to-help">
          <StepCard n={1} title="Report weather" icon={Camera} accent="#ea580c">
            Snap a photo of rain, storm, snow or damage. Add a note and hit
            submit — anywhere in the world, in about 20 seconds.
          </StepCard>
          <StepCard n={2} title="Verify a neighbour" icon={ThumbsUp} accent="#16a34a">
            See a report from someone near you? Tap 👍 to confirm. Three
            neighbour confirmations flip a report to <em>verified</em>.
          </StepCard>
          <StepCard n={3} title="Get local alerts" icon={BellRing} accent="#7c3aed">
            Turn on push notifications and we'll ping you when severe
            weather is coming, or when a nearby storm gets community-verified.
          </StepCard>
        </div>

        {/* Recent community activity strip */}
        {activity.length > 0 && (
          <div className="mt-8" data-testid="weather-hero-recent-activity">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs uppercase font-mono tracking-wider opacity-70">
                  RECENT COMMUNITY ACTIVITY
                </div>
                <h3 className="font-display text-xl mt-0.5">Real people, real conditions, right now</h3>
              </div>
              <Link to="/weather/community" className="text-sm hover:underline"
                    style={{ color: "var(--wofy-primary)" }}
                    data-testid="weather-hero-all-activity">
                View all →
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {activity.map((r) => {
                const Icon = REPORT_ICON[r.type] || Cloud;
                const sev = SEVERITY_COLOR[r.severity] || "#6b7280";
                return (
                  <Link key={r.id}
                        to={`/weather/report/${r.id}`}
                        className="flex-none w-64 rounded-2xl bg-white/90 backdrop-blur border overflow-hidden hover:shadow-md transition"
                        style={{ borderColor: "rgba(0,0,0,0.08)" }}
                        data-testid={`weather-activity-card-${r.id}`}>
                    {r.photo_urls?.[0] ? (
                      <div className="h-28 bg-black/5">
                        <img src={r.photo_urls[0]} alt={r.type}
                             className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="h-28 flex items-center justify-center"
                           style={{ background: `${sev}12` }}>
                        <Icon className="w-10 h-10" style={{ color: sev }} />
                      </div>
                    )}
                    <div className="p-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                              style={{ background: `${sev}22`, color: sev }}>
                          {_titleize(r.severity || "moderate")}
                        </span>
                        <span className="text-xs font-medium">{_titleize(r.type)}</span>
                      </div>
                      <div className="mt-1 text-xs opacity-80 truncate">
                        {[r.city, r.region, r.country].filter(Boolean).join(", ") || "Somewhere"}
                      </div>
                      {r.description && (
                        <div className="mt-1 text-xs opacity-70 line-clamp-2">{r.description}</div>
                      )}
                      <div className="mt-2 flex items-center justify-between text-[11px] opacity-60">
                        <span>{r.author_name || "Anonymous"}</span>
                        <span>{_ago(r.created_at)}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function StepCard({ n, title, icon: Icon, accent, children }) {
  return (
    <div className="rounded-2xl bg-white/80 backdrop-blur border p-4"
         style={{ borderColor: "rgba(0,0,0,0.08)" }}
         data-testid={`weather-step-${n}`}>
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full flex items-center justify-center font-display font-bold text-sm"
              style={{ background: `${accent}15`, color: accent }}>
          {n}
        </span>
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <span className="font-medium">{title}</span>
      </div>
      <p className="mt-2 text-sm opacity-80">{children}</p>
    </div>
  );
}
